import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api/client.js';

const STATUS_BADGE = {
  pending: 'undet',
  compiling: 'blue',
  compiled: 'success',
  active: 'success',
  inactive: 'blue',
  failed: 'error'
};

export default function SpecsView() {
  const [specs, setSpecs] = useState([]);
  const [label, setLabel] = useState('');
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const data = await api.listSpecs();
      const list = data.items ?? data.specs ?? (Array.isArray(data) ? data : []);
      setSpecs(list);
    } catch (e) {
      setError(`Load failed: ${e.message}`);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!label.trim() || !file) { setError('Label and .xlsx file are both required'); return; }
    setBusy(true); setError(null); setStatus('Requesting upload URL…');
    try {
      const { url } = await api.uploadSpec(label.trim());
      setStatus('Uploading file to S3…');
      await api.uploadSpecFile(url, file);
      setStatus('✓ Uploaded. Compiling in background — list refreshes automatically.');
      setLabel(''); setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      load();
    } catch (e) {
      setError(`Upload failed: ${e.message}`); setStatus(null);
    } finally { setBusy(false); }
  };

  const handleActivate = async (specLabel) => {
    if (!confirm(`Activate spec "${specLabel}"? Requires dual approval (a different admin than the uploader).`)) return;
    try { await api.activateSpec(specLabel); load(); }
    catch (e) { setError(`Activate failed: ${e.message}`); }
  };

  return (
    <>
      <h1>Spec Management</h1>

      <div className="cb-card">
        <h2>Upload Workbook</h2>
        <form onSubmit={handleUpload}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="cb-field">
              <label htmlFor="spec-label">Label (e.g. v10-2025)</label>
              <input id="spec-label" type="text" value={label} onChange={(e) => setLabel(e.target.value)}
                pattern="[A-Za-z0-9._\-]{1,64}" placeholder="v10-2025" disabled={busy} required />
            </div>
            <div className="cb-field">
              <label htmlFor="spec-file">Excel file (.xlsx)</label>
              <input ref={fileRef} id="spec-file" type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={busy} required />
            </div>
          </div>
          <div className="cb-row" style={{ marginTop: 8 }}>
            <button type="submit" className="cb-btn primary sm" disabled={busy}>
              {busy ? 'Uploading…' : 'Upload'}
            </button>
            {status && <span className="cb-muted" style={{ fontSize: 12 }}>{status}</span>}
          </div>
          {error && <div className="cb-alert" role="alert" style={{ marginTop: 12 }}>{error}</div>}
        </form>
      </div>

      <div className="cb-row" style={{ justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ fontSize: 11, fontWeight: 700, color: 'var(--cb-gray-5)', textTransform: 'uppercase', letterSpacing: '0.8px', margin: 0 }}>
          Existing specs
        </h2>
        <button onClick={load} className="cb-btn sm">Refresh</button>
      </div>

      {specs.length === 0 ? (
        <div className="cb-empty">No specs uploaded yet.</div>
      ) : (
        <div className="cb-card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="cb-table">
            <thead>
              <tr>
                <th>Label</th><th>Status</th><th>Uploaded by</th><th>Uploaded at</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {specs.map((s) => {
                const lbl = s.label ?? (typeof s === 'string' ? s : '');
                return (
                  <tr key={lbl}>
                    <td style={{ fontFamily: '"Roboto Mono", monospace' }}>{lbl}</td>
                    <td><span className={`cb-badge ${STATUS_BADGE[s.status] ?? 'blue'}`}>{s.status || '—'}</span></td>
                    <td className="cb-muted" style={{ fontSize: 12 }}>{s.uploadedByEmail || '—'}</td>
                    <td className="cb-muted" style={{ fontSize: 12 }}>{s.uploadedAt ? new Date(s.uploadedAt).toLocaleString() : '—'}</td>
                    <td>
                      {s.status === 'compiled' && (
                        <button className="cb-btn sm primary" onClick={() => handleActivate(lbl)}>Activate</button>
                      )}
                      {s.status === 'failed' && s.error && (
                        <span className="cb-badge error" title={s.error}>⚠ {s.error.slice(0, 40)}{s.error.length > 40 ? '…' : ''}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
